import React, { useEffect, useState } from 'react';

/**
 * Full-screen image modal with keyboard nav (Esc, arrows) and accessible controls.
 */
function ImageModal({
  image,
  imageIndex,
  totalCount,
  onClose,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
}) {
  const [loadFailed, setLoadFailed] = useState(false);
  const src = image?.url || image?.signedUrl;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && hasPrev) onPrev();
      else if (e.key === 'ArrowRight' && hasNext) onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Powiększone zdjęcie"
    >
      <div
        className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label="Zamknij"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {hasPrev && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            className="absolute left-4 text-white hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-3 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Poprzednie zdjęcie"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {hasNext && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="absolute right-4 text-white hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-3 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Następne zdjęcie"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {loadFailed ? (
          <div
            className="max-w-full max-h-full flex items-center justify-center bg-gray-800 rounded-lg p-8"
            role="img"
            aria-label="Błąd ładowania zdjęcia"
          >
            <span className="text-gray-400">Błąd ładowania</span>
          </div>
        ) : (
          <img
            src={src}
            alt={image?.filename ? `Zdjęcie ${imageIndex + 1}: ${image.filename}` : `Zdjęcie ${imageIndex + 1}`}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
            onError={() => setLoadFailed(true)}
          />
        )}

        {totalCount > 1 && (
          <div
            className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg text-sm"
            aria-live="polite"
          >
            {imageIndex + 1} / {totalCount}
          </div>
        )}

        {image?.filename && (
          <div
            className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg text-sm max-w-md truncate"
            title={image.filename}
          >
            {image.filename}
          </div>
        )}
      </div>
    </div>
  );
}

export default ImageModal;
